import type { IntlShape } from "@formatjs/intl";
import {
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Row,
  Space,
  type FormInstance,
} from "antd";
import type {
  Blade,
  Client,
  Location,
  Materials,
  Project,
  Services,
  Status,
  Turbine,
  UserLimitedList,
} from "../generated-axios-client";
import { ServiceSelectorFormItem } from "./create-forms/ServiceSelectorFormItem";
import { MultiAssigneeSelectorFormItem } from "./create-forms/MultiAssigneeSelectorFormItem";
import { CoordinatorSelectorFormItem } from "./create-forms/CoordinatorSelectorFormItem";
import { StatusSelectorFormItem } from "./create-forms/StatusSelectorFormItem";
import { ClientSelectorFormItem } from "./create-forms/ClientSelectorFormItem";
import { TurbineSelectorFormItem } from "./create-forms/TurbineSelectorFormItem";
import { TaskSelectorFormItem } from "./create-forms/TaskSelectorFormItem";
import { LocationSelectorFormItem } from "./create-forms/LocationSelectorFormItem";
import { MaterialsSelectorFormItem } from "./create-forms/MaterialsSelectorFormItem";
import { ProjectSelectorFormItem } from "../../common/components/ProjectSelect";

type UpdateTicketFieldsProps = {
  intl: IntlShape;
  serviceItems: Services[];
  userItems: UserLimitedList[];
  coordinatorItems: UserLimitedList[];
  statusItems: Status[];
  turbineItems: Turbine[];
  clientItems: Client[];
  bladeItems?: Blade[];
  locationItems: Location[];
  materialItems: Materials[];
  projectItems: Project[];
  form: FormInstance;
};

export const UpdateTicketFields = (props: UpdateTicketFieldsProps) => {
  const {
    intl,
    serviceItems,
    userItems,
    coordinatorItems,
    statusItems,
    turbineItems,
    clientItems,
    bladeItems,
    locationItems,
    materialItems,
    projectItems,
    form,
  } = props;

  return (
    <Flex vertical gap="middle">
      <Card title={intl.formatMessage({ id: "ticket_section_details" })}>
        <Row gutter={24}>
          <Col xs={24} md={8}>
            <ServiceSelectorFormItem intl={intl} serviceItems={serviceItems} />
          </Col>
          {/* <Col xs={24} md={8}>
            <PrioritySelectorFormItem intl={intl} priorityItems={priorityItems} />
          </Col> */}
          <Col xs={24} md={8}>
            <StatusSelectorFormItem intl={intl} statusItems={statusItems} />
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              name="description"
              label={intl.formatMessage({ id: "ticket_description" })}
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "validation_message_ticket_description",
                  }),
                },
              ]}
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} md={6}>
            <ClientSelectorFormItem intl={intl} clientItems={clientItems} form={form} />
          </Col>
          <Col xs={24} md={6}>
            <TurbineSelectorFormItem
              intl={intl}
              turbineItems={turbineItems}
              form={form}
            />
          </Col>
          <Col xs={24} md={6}>
            <Form.Item
              name="client_case_nr"
              label={intl.formatMessage({ id: "ticket_client_case_number" })}
              rules={[
                {
                  required: true,
                  // pattern: /^\d{7}?/,
                  message: intl.formatMessage({
                    id: "validation_message_ticket_client_case_number",
                  }),
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <ProjectSelectorFormItem
              intl={intl}
              form={form}
              projectItems={projectItems}
              placeholder={intl.formatMessage({ id: "project_field_project_placeholder" })}
            />
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Form.Item label={intl.formatMessage({ id: "ticket_start" })} name="start">
              <DatePicker
                showNow={false}
                style={{ width: "100%" }}
                placeholder={intl.formatMessage({ id: "ticket_start" })}
                format="DD/MM/YYYY"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={intl.formatMessage({ id: "ticket_deadline" })}
              name="deadline"
            >
              <DatePicker
                showNow={false}
                style={{ width: "100%" }}
                placeholder={intl.formatMessage({ id: "ticket_deadline" })}
                format="DD/MM/YYYY"
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card title={intl.formatMessage({ id: "ticket_section_assignment" })}>
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <CoordinatorSelectorFormItem intl={intl} userItems={coordinatorItems} />
          </Col>
          <Col xs={24} md={12}>
            <LocationSelectorFormItem
              intl={intl}
              locationItems={locationItems}
              turbineItems={turbineItems}
              form={form}
            />
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24}>
            <MultiAssigneeSelectorFormItem intl={intl} userItems={userItems} />
          </Col>
        </Row>
      </Card>

      <Card title={intl.formatMessage({ id: "ticket_tasks" })}>
        <TaskSelectorFormItem
          intl={intl}
          bladeItems={bladeItems}
          serviceItems={serviceItems}
          form={form}
        />
      </Card>

      <Card title={intl.formatMessage({ id: "ticket_section_supplies" })}>
        <MaterialsSelectorFormItem intl={intl} materialItems={materialItems} />
      </Card>

      <Form.Item name="id" style={{ display: "none" }}>
        <Input />
      </Form.Item>
    </Flex>
  );
};
